/**
 * TW PDP Rating — scroll to Yotpo reviews widget on star-rating click.
 *
 * The click is delegated from `document` so it keeps working after the combined-listing
 * block swaps the PDP sections for a sibling product. What does NOT survive that swap is
 * the Yotpo widget itself: it is re-mounted asynchronously (see tw-combined-listing.js),
 * so for a moment the reviews section exists but is empty. Scrolling to a zero-height
 * section reads to a shopper as "the click did nothing", so the target has to be rendered
 * — not merely present — before we move the page.
 */
(function () {
  const HEADER_BUFFER = 16;
  const FALLBACK_HEADER_OFFSET = 80;
  // A mounted reviews widget is hundreds of pixels tall; an un-mounted placeholder is a
  // few at most. Anything under this is treated as "still loading".
  const MIN_RENDERED_HEIGHT = 48;
  // Backoff while the widget mounts. Roughly 5s in total, which covers a cold Yotpo load.
  const RETRY_DELAYS = [120, 250, 400, 600, 900, 1200, 1500];
  // The widget keeps growing as its images and stars paint, which moves the target under
  // the shopper. These re-checks nudge the page back onto it.
  const REALIGN_DELAYS = [700, 1600];
  const REALIGN_TOLERANCE = 60;
  // The first three only exist once the review app has MOUNTED, so they disappear the
  // moment a section is re-rendered client-side. The placeholder is server-rendered and
  // therefore findable immediately after a swap — data-yotpo-product-id narrows it to the
  // product reviews widget rather than any other Yotpo widget on the page.
  const YOTPO_WIDGET_SELECTORS = [
    '.yotpo-main-widget',
    '#yotpo-reviews-main-widget',
    '.yotpo-reviews-main-widget',
    '.yotpo-widget-instance[data-yotpo-product-id]',
  ].join(', ');

  let realignTimers = [];
  let cancelRealign = null;

  function getHeaderOffset() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    const parsed = parseFloat(raw);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed + HEADER_BUFFER;
    }
    return FALLBACK_HEADER_OFFSET;
  }

  function isVisible(el) {
    if (!el) return false;
    const section = el.closest('.shopify-section');
    if (section && section.offsetParent === null) return false;
    return el.offsetParent !== null || el.getClientRects().length > 0;
  }

  function findReviewsTarget() {
    for (const anchor of document.querySelectorAll('[data-tw-reviews-target]')) {
      if (!isVisible(anchor)) continue;
      return anchor.closest('.shopify-section') || anchor;
    }

    const mainProduct = document.querySelector('[id^="MainProduct-"], product-info');
    for (const widget of document.querySelectorAll(YOTPO_WIDGET_SELECTORS)) {
      const section = widget.closest('.shopify-section');
      if (!section) continue;
      if (mainProduct && mainProduct.contains(section)) continue;
      if (!isVisible(section)) continue;
      return section;
    }

    return null;
  }

  function isRendered(target) {
    return target.offsetHeight >= MIN_RENDERED_HEIGHT;
  }

  function desiredScrollTop(target) {
    return Math.max(0, target.getBoundingClientRect().top + window.scrollY - getHeaderOffset());
  }

  function stopRealign() {
    realignTimers.forEach(clearTimeout);
    realignTimers = [];
    if (cancelRealign) {
      cancelRealign();
      cancelRealign = null;
    }
  }

  /**
   * Re-aim at the target as the widget finishes painting — but only while the shopper is
   * still a passenger. The moment they scroll themselves, the page is theirs.
   */
  function realignWhileRendering(target) {
    let abandoned = false;
    const onUserScroll = () => {
      abandoned = true;
    };
    const events = ['wheel', 'touchstart', 'keydown'];
    events.forEach((type) => window.addEventListener(type, onUserScroll, { passive: true }));
    cancelRealign = () => events.forEach((type) => window.removeEventListener(type, onUserScroll));

    realignTimers = REALIGN_DELAYS.map((delay, index) =>
      setTimeout(() => {
        if (abandoned || !target.isConnected) return;
        const top = desiredScrollTop(target);
        if (Math.abs(top - window.scrollY) > REALIGN_TOLERANCE) {
          window.scrollTo({ top: top, behavior: 'smooth' });
        }
        if (index === REALIGN_DELAYS.length - 1) stopRealign();
      }, delay)
    );
  }

  function scrollToReviews(target) {
    stopRealign();
    window.scrollTo({ top: desiredScrollTop(target), behavior: 'smooth' });
    if (history.replaceState) {
      history.replaceState(null, '', '#reviews');
    } else {
      window.location.hash = 'reviews';
    }
    realignWhileRendering(target);
  }

  function scrollToReviewsWithRetry(attempt) {
    const target = findReviewsTarget();

    if (target && isRendered(target)) {
      scrollToReviews(target);
      return true;
    }

    if (attempt < RETRY_DELAYS.length) {
      setTimeout(() => scrollToReviewsWithRetry(attempt + 1), RETRY_DELAYS[attempt]);
      return false;
    }

    // Out of patience. A section that never grew is still a better destination than
    // leaving the shopper where they clicked.
    if (target) {
      scrollToReviews(target);
      return true;
    }

    return false;
  }

  function onClick(event) {
    const trigger = event.target.closest('[data-tw-scroll-to-reviews]');
    if (!trigger) return;

    event.preventDefault();
    scrollToReviewsWithRetry(0);
  }

  document.addEventListener('click', onClick);
})();
